import { FC, useEffect, useMemo, useRef, useState } from 'react'
import {
  Paper,
  Box,
  CircularProgress,
  IconButton,
  Stack,
  styled,
  TableContainer,
  Table,
  TableBody,
} from '@mui/material'
import ViewIcon from '@/assets/icons/actions/view.svg'
import EditIcon from '@/assets/icons/actions/edit.svg'
import {
  TableWrapper,
  TableContainerStyled,
  VirtualTableContainer,
  VirtualRow,
  VirtualCell,
} from '@/components/table/StyledTableComponents'
import { TableHeader } from '@/components/table/TableHeader'
import { TableFooter } from '@/components/table/TableFooter'
import { NoResultsAlert } from '@/components/notification/NoResultsAlert'
import type { SortableTableProps } from '@/utilities/types/table-props'
import type {
  TableColumn,
  SupervisorData,
  AreaControllerData,
} from '@/utilities/types/table'
import ViewAreaControllerCollumnChips from '@/components/modal/area-management/supervisors/ViewAreaControllerCollumnChips'
import ViewSupervisorModal from '@/components/modal/area-management/supervisors/ViewSupervisorModal'
import { AssignAreaControllerModal } from '@/components/modal/area-management/supervisors/AssignAreaControllerModal'
import { assignSupervisor } from '@/api'
import { useSupervisorVisualizedTable } from '@/hooks/shared/useSupervisorVisualizedTable'
import dayjs from 'dayjs'
import { SupervisorTableFooter } from '../../SupervisorTableFooter'
import { AxiosError } from 'axios'

export const StyledRow = styled(Box)(() => ({
  display: 'flex',
  width: '100%',
  height: '52px',
  padding: '16px',
  '&:nth-of-type(even)': {
    backgroundColor: '#f5f5f5',
  },
  '&:nth-of-type(odd)': {
    backgroundColor: 'white',
  },
}))

export const StyledCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  wordBreak: 'break-word',
  textAlign: 'center',
  fontSize: '12px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
  },
}))

const LoadingContainer = styled(Box)({
  p: 4,
  textAlign: 'center',
  height: 'calc(90vh - 60px)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
})

const columns: TableColumn[] = [
  { id: 'sourceUserId', label: 'ID', width: '10%', sortable: true },
  { id: 'user', label: 'Name', width: '25%', sortable: true },
  {
    id: 'areaController',
    label: 'Area Controller',
    width: '25%',
    sortable: false,
  },
  { id: 'dateCreated', label: 'Created Date', width: '15%', sortable: true },
  { id: 'dateModified', label: 'Modified Date', width: '15%', sortable: true },
  {
    id: 'action',
    label: 'Action',
    width: '10%',
    align: 'center',
    sortable: false,
  },
]

const preprocessData = (data: SupervisorData[]) => {
  if (!data) return []

  return data.map((item) => ({
    ...item,
    user: `${item.firstName} ${item.lastName}`,
    areaController: (item.userAreaControllers || [])
      .map((controller) => `${controller.firstName} ${controller.lastName}`)
      .join(', '),
  }))
}

interface ExtendedSupervisorTableProps
  extends SortableTableProps<SupervisorData> {
  areaControllers?: AreaControllerData[]
  onSupervisorAssigned?: () => Promise<void>
  refreshTable: () => Promise<void>
}

export const AreaManagementSupervisorTable: FC<
  ExtendedSupervisorTableProps
> = ({
  data = [],
  filterState,
  isLoading,
  error,
  onSort,
  areaControllers = [],
  onSupervisorAssigned,
  refreshTable,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  totalElem,
  handleSort
}) => {
  const processedData = useMemo(() => preprocessData(data), [data])
  const parentRef = useRef<HTMLDivElement>(null)
  const [showNoResults, setShowNoResults] = useState(false)
  const [viewModalOpen, setViewModalOpen] = useState(false)
  const [assignModalOpen, setAssignModalOpen] = useState(false)
  const [selectedSupervisor, setSelectedSupervisor] =
    useState<SupervisorData | null>(null)
  const [assignFormData, setAssignFormData] = useState({
    supervisor: '',
    userAreaControllerIds: [] as string[],
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [assignError, setAssignError] = useState<string | null>(null)

  const {
    virtualRows,
    totalSize,
    paginatedItems,
    handlePageChange,
    handleRowsPerPageChange,
    handleVirtualizedScroll,
  } = useSupervisorVisualizedTable({
    data,
    filterState,
    containerRef: parentRef,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    totalElem,
  })

  const handleView = (supervisor: SupervisorData) => {
    setSelectedSupervisor(supervisor)
    setViewModalOpen(true)
  }

  const handleAssign = (supervisor: SupervisorData) => {
    console.log('Opening assign modal for supervisor:', supervisor)
    setSelectedSupervisor(supervisor)

    const currentControllerIds = (supervisor.userAreaControllers || []).map(
      (controller) => controller.sourceUserId.toString(),
    )

    console.log('Setting current controller IDs:', currentControllerIds)
    setAssignFormData({
      supervisor: `${supervisor.firstName} ${supervisor.lastName}`,
      userAreaControllerIds: currentControllerIds,
    })
    setAssignModalOpen(true)
  }

  const handleAssignSubmit = async (formData: {
    supervisor: string
    userAreaControllerIds: string[]
  }) => {
    if (!selectedSupervisor) return

    setIsSubmitting(true)
    setAssignError(null)

    try {
      console.log('Submitting assignment:', {
        supervisorId: selectedSupervisor.sourceUserId,
        controllerIds: formData.userAreaControllerIds,
      })

      const parsedIds = formData.userAreaControllerIds.map((id) =>
        parseInt(id, 10),
      )

      await assignSupervisor({
        sourceUserId: selectedSupervisor.sourceUserId,
        userAreaControllerIds: parsedIds,
      })

      setAssignModalOpen(false)
      setSelectedSupervisor(null)
      setAssignFormData({
        supervisor: '',
        userAreaControllerIds: [],
      })

      if (onSupervisorAssigned) {
        await onSupervisorAssigned()
      }
      await refreshTable()
    } catch (error) {
      if (error instanceof AxiosError) {
        let newError = error.response?.data.message || 'No Assigned Areas found'
        setAssignError(newError)
      } else {
        setAssignError(
          error instanceof Error
            ? error.message
            : 'Failed to assign area controllers',
        )
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          height: '70vh',
          width: '100%',
          background: 'white',
          zIndex: 1,
          display: 'fleex',
          justifyContent: 'center',
          alignItems: 'center',
          borderBottomLeftRadius: '10px',
          borderBottomRightRadius: '10px',
          marginTop: '2px',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>
        Error: {error instanceof Error ? error.message : 'Unknown error'}
      </Box>
    )
  }

  return (
    <div
      style={{
        boxShadow: 'none',
        width: '100%',
        zIndex: 1,
        marginTop: '2px',
      }}
    >
      <TableContainer component={Paper}>
        <Table
          sx={{ minWidth: 650, minHeight: '600px' }}
          aria-label="simple table"
        >
          <TableHeader
            columns={columns}
            filterState={filterState}
            onSort={onSort}
            handleSort={handleSort}
          />
          <TableBody
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            {paginatedItems.map((row, index) => (
              <StyledRow key={index}>
                {columns.map((column) => {
                  if (column.id === 'sourceUserId') {
                    return (
                      <StyledCell style={{ width: column.width }}>
                        {row.sourceUserId}
                      </StyledCell>
                    )
                  }
                  if (column.id === 'user') {
                    return (
                      <StyledCell width={column.width}>
                        {`${row.firstName} ${row.lastName}`}
                      </StyledCell>
                    )
                  }

                  if (column.id === 'areaController') {
                    return (
                      <StyledCell width={column.width}>
                        <ViewAreaControllerCollumnChips
                          userAreaControllers={row.userAreaControllers || []}
                        />
                      </StyledCell>
                    )
                  }

                  if (column.id === 'dateCreated') {
                    return (
                      <StyledCell width={column.width}>
                        {dayjs(row.dateCreated).format('MMM DD, YYYY')}
                      </StyledCell>
                    )
                  }
                  if (column.id === 'dateModified') {
                    return (
                      <StyledCell width={column.width}>
                        {dayjs(row.dateModified).format('MMM DD, YYYY')}
                      </StyledCell>
                    )
                  }
                  if (column.id === 'action') {
                    return (
                      <StyledCell width={column.width}>
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="center"
                        >
                          <IconButton
                            onClick={() => handleView(row)}
                            sx={{
                              padding: '4px',
                              '&:hover': {
                                bgcolor: 'rgba(73, 90, 255, 0.05)',
                              },
                            }}
                          >
                            <img
                              src={ViewIcon}
                              alt="View"
                              width={16}
                              height={16}
                            />
                          </IconButton>
                          <IconButton
                            onClick={() => handleAssign(row)}
                            sx={{
                              padding: '4px',
                              '&:hover': {
                                bgcolor: 'rgba(73, 90, 255, 0.05)',
                              },
                            }}
                          >
                            <img
                              src={EditIcon}
                              alt="Assign"
                              width={16}
                              height={16}
                            />
                          </IconButton>
                        </Stack>
                      </StyledCell>
                    )
                  }
                })}
              </StyledRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ViewSupervisorModal
        open={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false)
          setSelectedSupervisor(null)
        }}
        data={selectedSupervisor}
      />

      {selectedSupervisor && (
        <AssignAreaControllerModal
          open={assignModalOpen}
          onClose={() => {
            setAssignModalOpen(false)
            setSelectedSupervisor(null)
            setAssignError(null)
          }}
          onSubmit={handleAssignSubmit}
          formData={assignFormData}
          setFormData={setAssignFormData}
          areaControllers={areaControllers}
          existingControllers={selectedSupervisor.userAreaControllers || []}
          supervisorName={`${selectedSupervisor.firstName} ${selectedSupervisor.lastName}`}
          isLoading={isSubmitting}
          error={assignError}
        />
      )}
      <SupervisorTableFooter
        rowsPerPage={rowsPerPage}
        totalCount={totalElem}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </div>
  )
}
